import React from 'react'
import { Collapsible } from '~/components/Collapsible'
import { Capsule, Chip, Coco, Pot, Seeds, StartKit } from '~/components/Icons'
import { useTranslations } from '~/components/Locale'
import type { SimpleVariant } from '~/types'
import { PRODUCT_IDS } from '~/utils'

interface Props {
	productId: ProductID
	upsells?: SimpleVariant[]
}

export const InTheBox: React.FC<Props> = ({ productId, upsells }) => {
	const contents = useBoxContents(productId, upsells)
	const t = useTranslations('react')
	return (
		<div className="bg-gray-100 p-4 py-20 text-center lg:px-20">
			<div className="mx-auto max-w-screen-lg">
				<h2 className="text-5xl">{t?.auk_page.whats_in_the_box}</h2>
				<ul className="list grid grid-cols-3 grid-rows-[auto,auto]">
					{contents.map((item, i) => (
						<li key={i} className="p-4">
							<img className="inline-block w-24 p-1" src={item.icon} />
							<h3 className="mb-2 text-3xl leading-none">
								{item.quantity} x {item.name}
							</h3>
							<p className="my-0">{item.description}</p>
							{item.extraInfo && (
								<Collapsible
									title={t?.products.info}
									summaryClassname="text-lg opacity-60 hover:opacity-100"
								>
									<p className="my-0 text-xl">{item.extraInfo}</p>
								</Collapsible>
							)}
						</li>
					))}
				</ul>
			</div>
		</div>
	)
}

interface BoxContent {
	quantity: number
	name: React.ReactNode
	description: React.ReactNode
	extraInfo?: React.ReactNode
	icon: string
}

function useBoxContents(productId: ProductID, upsells?: SimpleVariant[]): BoxContent[] {
	const t = useTranslations('react.auk_page')
	if (productId === PRODUCT_IDS.Auk && upsells) {
		const nutrients = upsells.find(u => u.product.id === PRODUCT_IDS.ExtraNutrients)
		const chiliKit = upsells.find(u => u.product.id === PRODUCT_IDS.ChiliKit)
		return [
			{ quantity: 1, name: 'Auk', description: t?.auk_desc, icon: StartKit },
			{ quantity: 6, name: t?.pots, description: t?.pots_desc, icon: Pot },
			{ quantity: 6, name: t?.coconut_soil, description: t?.coconut_soil_desc, icon: Coco },
			{
				quantity: nutrients ? 9 : 3,
				name: t?.nutrients,
				description: nutrients ? t?.nutrients_desc_upsell : t?.nutrients_desc,
				icon: Capsule,
				extraInfo: t?.nutrients_extra_info
			},
			{
				quantity: chiliKit ? 2 : 1,
				name: t?.plant_program,
				description: chiliKit ? t?.plant_program_desc_upsell : t?.plant_program_desc,
				icon: Chip,
				extraInfo: t?.plant_program_extra_info
			},
			{
				quantity: chiliKit ? 5 : 3,
				name: t?.seed_packets,
				description: chiliKit ? t?.seed_packets_desc_upsell : t?.seed_packets_desc,
				icon: Seeds,
				extraInfo: t?.seed_packets_extra_info
			}
		]
	} else {
		return []
	}
}
